/**
 * One vocabulary for re-running the processing pipeline on a scan, shared by
 * the project page kebab and both viewer menus (they had three label sets).
 * Reprocess is admin-only and deliberately one click — no confirmation.
 *
 * NOTE: the two VIEWER menus render `Object.values(REPROCESS_VARIANTS)`, so a
 * new variant appears there for free. The project-page kebab still hand-rolls a
 * button per variant and currently offers only `standard` + `align` — a new
 * variant does NOT appear there until that menu is enumerated too (issue #1285).
 *
 * ## `depthRefinement` is explicit on EVERY variant, deliberately
 *
 * Each button does what its label says, whatever the `depthRefinement` feature
 * flag is set to. An earlier version left it `undefined` on the older variants, so they
 * INHERITED the flag — which meant "Reprocess with Wall Alignment" silently
 * started running a two-pass cycle the moment the flag was turned on, under a
 * label that never mentioned it. A deliberate admin action should not change
 * behaviour because of an environment variable.
 *
 * That leaves the flag governing exactly what only it can: the AUTOMATIC path.
 * A new scan is processed by a call from the iOS app that sends no options, so
 * the flag is the only thing that can put new scans into the cycle.
 *
 * ## What "(2-pass)" means, and what it does not
 *
 * It selects the CYCLE — draft, measure against the raw LiDAR, reprocess. The
 * measured rows standing on a scan are applied ONLY in the cycle's LiDAR stage
 * (pass 2), right after the measurement that replaced them
 * (`appliesStandingRows` in structure-processing, and
 * `depth-finalization-sweep`'s `replaceRows`). Every other run builds from the
 * scan alone: the draft (pass 1), and every variant here that does not measure.
 * Nothing deletes the rows — they stay on the scan to be read.
 */
import { SCAN_STAGE } from '../constants/scanStages';

export const REPROCESS_VARIANTS = {
  standard: {
    key: 'standard',
    label: 'Reprocess',
    runWallAlignment: false,
    skipBathroomPostProcessing: false,
    depthRefinement: false,
  },
  // The A/B baseline: the full pipeline with NO LiDAR input at all. A run that
  // does not measure applies no stored rows anyway; `useDepthRows: false` says
  // so explicitly, and would hold even if that default changed (see
  // useDepthRows in structure-processing).
  //
  // Nothing is deleted: the rows stay on the scan, and a refinement's LiDAR
  // stage applies them again.
  align: {
    key: 'align',
    label: 'Reprocess with Wall Alignment (no LiDAR)',
    runWallAlignment: true,
    skipBathroomPostProcessing: false,
    depthRefinement: false,
    useDepthRows: false,
  },
  skipBathroom: {
    key: 'skipBathroom',
    label: 'Reprocess (Skip Bathroom)',
    runWallAlignment: false,
    skipBathroomPostProcessing: true,
    depthRefinement: false,
  },
  // Wall alignment is not optional here: the depth rows are consumed INSIDE
  // wall processing, so a refinement run without it would finalize having
  // applied no measurement at all.
  // This is the only variant that ACTS on a refutation. The measurement is what makes
  // the removal defensible, so the two travel together: a run that does not measure has
  // nothing to remove on, and a run that measures and then only records leaves a shower
  // standing that the scan says is not there.
  // Stage 4 on its own: the fixture check, landed, and the cycle ends there
  // (`stoppedAfter: 'fixture-check'`). 'reuse' applies the answers the scan already
  // carries and asks the model only when there are none; 'rerun' asks again and
  // replaces them. Wall alignment as for the LiDAR stage: the found fixtures are
  // placed by the same passes that seat captured ones.
  fixtureCheck: {
    key: 'fixtureCheck',
    label: `Reprocess through ${SCAN_STAGE['fixture-check']} (reuse)`,
    runWallAlignment: true,
    skipBathroomPostProcessing: false,
    depthRefinement: true,
    fixtureCheck: 'reuse',
  },
  fixtureCheckRerun: {
    key: 'fixtureCheckRerun',
    label: `Reprocess through ${SCAN_STAGE['fixture-check']} (rerun)`,
    runWallAlignment: true,
    skipBathroomPostProcessing: false,
    depthRefinement: true,
    fixtureCheck: 'rerun',
  },
  lidarRefine: {
    key: 'lidarRefine',
    label: `Reprocess through ${SCAN_STAGE['refined-structure']}`,
    runWallAlignment: true,
    skipBathroomPostProcessing: false,
    depthRefinement: true,
    applyFixtureRefutations: true,
    // The mirror of the refutation, and it rides the same button for the same reason: a
    // bathroom boundary RoomPlan claims and none of our rooms covers is only defensibly
    // turned into a real shower because a measurement found wet walls around it. The
    // server has always accepted this flag; no variant sent it, so a confirmed boundary
    // was measured, seeded and then never placed by any button in the app.
    applyBoundaryConfirmations: true,
    // Same argument as the refutation above, mirrored: a box the bathroom sweep would
    // delete (or leave standing as bare `storage` / `table`) is only defensibly turned
    // back into a vanity because a measurement found a basin on it. So the measurement
    // and the recovery travel together on this one button, and nowhere else.
    applyVanityRecoveries: true,
  },
};

/**
 * The `processStructure` payload for a variant.
 *
 * ONE builder because there were three, hand-rolled, in the project kebab and both
 * viewer menus — and the viewers' copies never forwarded `applyFixtureRefutations`. The
 * viewer menus are the only place `lidarRefine` is even offered (the kebab exposes
 * `standard` + `align`), so the flag that gates every destructive LiDAR action was
 * unreachable from the UI: the button ran the two-pass cycle and then recorded without
 * acting, with nothing saying so. A variant's flags are its contract; assembling that
 * contract in three places is how one of them silently loses a field.
 *
 * Flags are passed through as-is, INCLUDING `undefined` — the server reads only an
 * explicit `true` for the act flags and follows its own rollout flag when
 * `depthRefinement` is absent.
 */
export function reprocessPayload(scanId, variant, extra = {}) {
  return {
    scanId,
    runWallAlignment: variant.runWallAlignment,
    skipBathroomPostProcessing: variant.skipBathroomPostProcessing,
    depthRefinement: variant.depthRefinement,
    useDepthRows: variant.useDepthRows,
    applyFixtureRefutations: variant.applyFixtureRefutations,
    applyBoundaryConfirmations: variant.applyBoundaryConfirmations,
    applyVanityRecoveries: variant.applyVanityRecoveries,
    fixtureCheck: variant.fixtureCheck,
    ...extra,
  };
}

/** ConfirmDialog copy for re-merging a merged structure from its sources. */
export const remergeConfirm = (scanName, sourceCount) => ({
  title: 'Re-merge Scans',
  message: `Re-merge ${sourceCount} source scan${sourceCount === 1 ? '' : 's'} and overwrite "${scanName || 'this merged scan'}"? Room edits made here since the merge will be regenerated.`,
  confirmText: 'Re-merge',
  danger: true,
});
